import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitReport, resetReportStatus } from '../../redux/slices/userAuditSlice';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Select } from 'antd';
import { toast } from 'react-toastify';
import { submitAuditReport, getOldReports } from '../../services/auditAPI';
import { getAutoFilledFields } from '../../services/geminiService'; // Assuming this service exists
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun } from 'docx';
import { Dropdown, Menu } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { ShadingType } from 'docx';
import {  Spin } from 'antd'; // Import Spin
import { LoadingOutlined } from "@ant-design/icons"; // Import LoadingOutlined

// Define antIcon here, outside the form but within the component's scope
const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
// import * as XLSXStyle from 'xlsx-style';

const getCellWithRiskColor = (value) => {
  let color = 'FFFFFF';
  if (['Very High', 'High', 1, 2].includes(value)) color = 'FF0000';
  else if (['Medium', 3].includes(value)) color = 'FFFF00';
  else if (['Low', 'Very Low', 4, 5].includes(value)) color = '00FF00';

  return new TableCell({
    children: [new Paragraph(String(value))],
    shading: {
      type: ShadingType.CLEAR,
      fill: color,
      color: 'auto',
    },
  });
};
const { Option } = Select;

function useDebouncedEffect(effect, deps, delay) {
  const callback = useRef();
  useEffect(() => {
    callback.current = effect;
  }, [effect]);

  useEffect(() => {
    const handler = setTimeout(() => {
      callback.current();
    }, delay);

    return () => clearTimeout(handler);
  }, [...(deps || []), delay]);
}

const getRiskColor = (risk) => {
  const val = risk.toLowerCase();
  if (val.includes('very high') || val.includes('high')) return 'bg-red-200';
  if (val.includes('medium')) return 'bg-yellow-200';
  if (val.includes('low') || val.includes('very low')) return 'bg-green-200';
  return '';
};

const SubmitReportPage = () => {
  const { auditId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, success, error } = useSelector((state) => state.userAudit);
  const userId = useSelector((state) => state.profile.user._id);

  const [reportEntries, setReportEntries] = useState([
    { observation: '', element: '', recommendation: '', risk: '', riskScore: '', legalStandard: '' }
  ]);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('submit');
  const [oldReports, setOldReports] = useState([]);
  const [debouncedObservationIndexes, setDebouncedObservationIndexes] = useState(new Set());
  const [aiLoading, setAiLoading] = useState({}); // State to manage loading per entry

  useEffect(() => {
    if (activeTab === 'view') {
      const fetchOldReports = async () => {
        try {
          const response = await getOldReports(auditId);
          setOldReports(response.report || []);
        } catch (err) {
          console.error('Error fetching old reports:', err);
          toast.error('Failed to load old reports');
        }
      };
      fetchOldReports();
    }
  }, [activeTab, auditId]);

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Audit Report', 14, 10);

    autoTable(doc, {
      startY: 20,
      head: [['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score']],
      body: oldReports.map(r => [
        r.observation, r.element, r.legalStandard, r.recommendation, r.risk, r.riskScore
      ]),
      didParseCell: (data) => {
        const col = data.column.index;
        const cell = data.cell;
        const value = cell.raw;

        const isRed = ['Very High', 'High', 1, 2].includes(value);
        const isYellow = ['Medium', 3].includes(value);
        const isGreen = ['Low', 'Very Low', 4, 5].includes(value);

        if (col === 4 || col === 5) {
          if (isRed) cell.styles.fillColor = [255, 0, 0]; // red
          else if (isYellow) cell.styles.fillColor = [255, 255, 0]; // yellow
          else if (isGreen) cell.styles.fillColor = [0, 255, 0]; // green
        }
      }
    });

    doc.save('audit-report.pdf');
  };


  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet([]); // Start with an empty sheet

    // Define the header for the columns
    const header = ['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score'];
    XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: 'A1' });

    oldReports.forEach((r, i) => {
      // Only include the necessary columns: Observation, Element, Legal Standard, Recommendation, Risk, Risk Score
      const row = [
        r.observation,
        r.element,
        r.legalStandard,
        r.recommendation,
        r.risk,
        r.riskScore
      ];

      // Add the row data to the worksheet
      XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: `A${i + 2}` });

      // Color the 'Risk' and 'Risk Score' columns based on the risk matrix
      const riskCell = worksheet[`E${i + 2}`]; // Column E for 'Risk'
      const scoreCell = worksheet[`F${i + 2}`]; // Column F for 'Risk Score'

      [riskCell, scoreCell].forEach(cell => {
        const value = cell.v;

        // Define the color based on the value of 'Risk' or 'Risk Score'
        const red = { fill: { fgColor: { rgb: 'FF0000' } } };  // Red for 'Very High' or 'High' or 1, 2
        const yellow = { fill: { fgColor: { rgb: 'FFFF00' } } };  // Yellow for 'Medium' or 3
        const green = { fill: { fgColor: { rgb: '00FF00' } } };  // Green for 'Low' or 'Very Low' or 4, 5

        if (['Very High', 'High', 1, 2].includes(value)) {
          Object.assign(cell.s = {}, red);
        } else if (['Medium', 3].includes(value)) {
          Object.assign(cell.s = {}, yellow);
        } else if (['Low', 'Very Low', 4, 5].includes(value)) {
          Object.assign(cell.s = {}, green);
        }
      });
    });

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

    // Write the workbook to a binary string and convert it to a Blob for download
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

    // Trigger the download of the Excel file
    saveAs(data, 'audit-report.xlsx');
  };

  const downloadWord = async () => {
    const tableRows = oldReports.map((r) =>
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph(r.observation || '')] }),
          new TableCell({ children: [new Paragraph(r.element || '')] }),
          new TableCell({ children: [new Paragraph(r.legalStandard || '')] }),
          new TableCell({ children: [new Paragraph(r.recommendation || '')] }),
          getCellWithRiskColor(r.risk),
          getCellWithRiskColor(r.riskScore),
        ],
      })
    );

    const table = new Table({
      rows: [
        new TableRow({
          children: ['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score'].map(
            heading => new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: heading, bold: true })] })] })
          ),
        }),
        ...tableRows,
      ],
    });

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [new Paragraph('Audit Report'), table],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, 'audit-report.docx');
  };

  const menu = (
    <Menu>
      <Menu.Item key="pdf" onClick={downloadPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="excel" onClick={downloadExcel}>
        Download Excel
      </Menu.Item>
      <Menu.Item key="word" onClick={downloadWord}>
        Download Word
      </Menu.Item>
    </Menu>
  )

  const handleChange = (index, field, value) => {
    const newEntries = [...reportEntries];
    newEntries[index][field] = value;
    setReportEntries(newEntries);

    // The debounced effect is already handling auto-fill based on observation changes
    // if (field === 'observation') {
    //   setDebouncedObservationIndexes((prev) => new Set([...prev, index]));
    // }
  };

  // Debounced effect for auto-filling based on observation changes (already present)
  useDebouncedEffect(() => {
    debouncedObservationIndexes.forEach(async (index) => {
      const obs = reportEntries[index].observation;
      if (obs.trim().length > 10) {
        try {
          setAiLoading(prev => ({ ...prev, [index]: true })); // Set loading for this entry
          const autoFields = await getAutoFilledFields(obs);
          if (autoFields) {
            const updatedEntries = [...reportEntries];
            updatedEntries[index] = {
              ...updatedEntries[index],
              element: autoFields.element,
              recommendation: autoFields.recommendation,
              risk: autoFields.risk,
              riskScore: autoFields.riskScore,
              legalStandard: autoFields.legalStandard || ''
            };
            setReportEntries(updatedEntries);
          }
        } catch (err) {
          console.error(`Auto-fill error at index ${index}:`, err);
          toast.error(`Failed to auto-fill entry ${index + 1}`);
        } finally {
          setAiLoading(prev => ({ ...prev, [index]: false })); // Unset loading
        }
      }
    });
    setDebouncedObservationIndexes(new Set());
  }, [reportEntries], 800);


  // New function to handle manual AI auto-fill button click
  const handleAutoFill = async (index) => {
    const obs = reportEntries[index].observation;
    if (obs.trim().length < 10) {
      toast.info("Please enter a more detailed observation to use AI auto-fill.");
      return;
    }

    try {
      setAiLoading(prev => ({ ...prev, [index]: true })); // Set loading for this entry
      const autoFields = await getAutoFilledFields(obs);
      if (autoFields) {
        const updatedEntries = [...reportEntries];
        updatedEntries[index] = {
          ...updatedEntries[index],
          element: autoFields.element,
          recommendation: autoFields.recommendation,
          risk: autoFields.risk,
          riskScore: autoFields.riskScore,
          legalStandard: autoFields.legalStandard || ''
        };
        setReportEntries(updatedEntries);
        toast.success(`Entry ${index + 1} auto-filled successfully!`);
      } else {
         toast.warn(`AI could not auto-fill entry ${index + 1}. Please try again or fill manually.`);
      }
    } catch (err) {
      console.error(`Manual Auto-fill error at index ${index}:`, err);
      toast.error(`Failed to auto-fill entry ${index + 1}`);
    } finally {
       setAiLoading(prev => ({ ...prev, [index]: false })); // Unset loading
    }
  };


  const handleAddEntry = () => {
    setReportEntries([
      ...reportEntries,
      { observation: '', element: '', recommendation: '', risk: '', riskScore: '', legalStandard: '' }
    ]);
    setExpandedIndex(reportEntries.length); // Expand new entry
  };

  const handleRemoveEntry = (index) => {
    const newEntries = [...reportEntries];
    newEntries.splice(index, 1);
    setReportEntries(newEntries);
    if (expandedIndex >= index && expandedIndex > 0) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    const payload = {
      submittedBy: userId,
      reportEntries: reportEntries.map((entry) => ({
        ...entry,
        riskScore: parseInt(entry.riskScore)
      }))
    };

    try {
      const response = await submitAuditReport(auditId, payload);
      if (response?.message === 'Report submitted successfully') {
        toast.success('Report submitted successfully!');
        dispatch(submitReport({ auditId, reportData: payload }));
        dispatch(resetReportStatus());
        setActiveTab('view');
        setReportEntries([
      { observation: '', element: '', recommendation: '', risk: '', riskScore: '', legalStandard: '' }
    ]);
    setExpandedIndex(0);
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      toast.error('Error submitting report');
      console.error("Submission Error:", err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-0">

      <h1 className="text-2xl font-bold mb-6">Submit Audit Report</h1>
      {/* <div className="flex justify-end mb-4">
        <Button onClick={() => navigate(`/audit/${auditId}/closure`)}>Closure</Button>
      </div> */}

      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => setActiveTab('submit')}
          className={activeTab === 'submit' ? 'bg-blue-500 text-white' : 'bg-gray-300'}
        >
          Submit Report
        </Button>
        <Button
          onClick={() => setActiveTab('view')}
          className={activeTab === 'view' ? 'bg-blue-500 text-white' : 'bg-gray-300'}
        >
          Old Reports
        </Button>
      </div>

      {activeTab === 'submit' && (
  <form onSubmit={handleSubmit} className=" space-y-2 lg:space-y-6 bg-white  rounded-lg">
    <h2 className="text-xl font-semibold text-gray-700 mb-6 border-b pb-2">Submit New Observations</h2> {/* Added border and padding */}
    {reportEntries.map((entry, index) => (
      <div
        key={index}
        className={`border border-gray-300 p-2 lg:p-6 md:p-4 rounded-lg shadow-sm transition-all duration-300 ease-in-out ${ // Increased padding here
          expandedIndex === index ? 'max-h-full opacity-100' : 'max-h-[3.5rem] overflow-hidden opacity-90 hover:opacity-100' // Adjust max-h and add hover effect
        }`}
      >
        {/* Collapsed/Expanded Header */}
        <div
          className="cursor-pointer font-semibold text-gray-700 mb-4 flex justify-between items-center" // Increased bottom margin
          onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)} // Collapse if already expanded
        >
          <span>Observation {index + 1}</span>
          <span className="transform transition-transform duration-300">{expandedIndex === index ? '▼' : '▶'}</span>
        </div>

        {/* Expanded Content */}
        {expandedIndex === index && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6"> {/* Increased vertical gap */}
            {/* Observation Field with AI Button */}
            <div className="flex flex-col col-span-1 md:col-span-2"> {/* Observation takes full width on small screens, half on medium+ */}
               <label htmlFor={`observation-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Observation</label>
              <div className="flex flex-col sm:flex-row gap-3"> {/* Increased gap */}
                <input
                  id={`observation-${index}`}
                  type="text"
                  placeholder="Enter observation details..."
                  value={entry.observation}
                  onChange={(e) => handleChange(index, 'observation', e.target.value)}
                  required
                  className="flex-grow border border-grey-300 outline-none rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2.5"
                />
                <Button
                  onClick={() => handleAutoFill(index)}
                  loading={aiLoading[index]}
                  disabled={aiLoading[index] || entry.observation.trim().length < 10}
                  className="flex-shrink-0 bg-green-500 hover:bg-green-600 text-white font-bold rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  icon={aiLoading[index] ? <Spin indicator={antIcon} /> : null} // Use Ant Design Spin
                >
                  {aiLoading[index] ? 'Auto-filling' : 'Get from AI'}
                </Button>
              </div>
            </div>

            {/* Other Fields */}
             <div>
               <label htmlFor={`element-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Element</label>
               <input
                 id={`element-${index}`}
                 type="text"
                 placeholder="Enter element..."
                 value={entry.element}
                 onChange={(e) => handleChange(index, 'element', e.target.value)}
                 required
                 className="form-input block w-full rounded-md border border-grey-300 outline-none shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2" // Improved input styling
               />
             </div>
             <div>
               <label htmlFor={`legalStandard-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Legal Standard</label>
               <input
                 id={`legalStandard-${index}`}
                 type="text"
                 placeholder="Enter legal standard..."
                 value={entry.legalStandard}
                 onChange={(e) => handleChange(index, 'legalStandard', e.target.value)}
                 className="form-input block w-full rounded-md border border-gray-300 outline-none p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" // Improved input styling
               />
             </div>
             <div className="col-span-1 md:col-span-2"> {/* Recommendation takes full width */}
               <label htmlFor={`recommendation-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Recommendation</label>
               <textarea
                 id={`recommendation-${index}`}
                 placeholder="Enter recommendation..."
                 value={entry.recommendation}
                 onChange={(e) => handleChange(index, 'recommendation', e.target.value)}
                 required
                 rows="3" // Give more space for recommendation
                 className="form-textarea block w-full rounded-md border border-gray-300 outline-none p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" // Improved textarea styling
               ></textarea>
             </div>


            <div>
              <label htmlFor={`risk-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label>
              <Select
                id={`risk-${index}`}
                placeholder="Select Risk"
                value={entry.risk}
                onChange={(value) => handleChange(index, 'risk', value)}
                className="w-full ant-select-override text-black= " // Use w-full and a custom class if needed
                required
              >
                <Option value="Very High">Very High</Option>
                <Option value="High">High</Option>
                <Option value="Medium">Medium</Option>
                <Option value="Low">Low</Option>
                <Option value="Very Low">Very Low</Option>
              </Select>
            </div>
            <div>
              <label htmlFor={`riskScore-${index}`} className="block text-sm font-medium text-gray-700 mb-1">Risk Score (1-5)</label>
              <input
                id={`riskScore-${index}`}
                type="number"
                placeholder="Enter score (1-5)"
                value={entry.riskScore}
                onChange={(e) => handleChange(index, 'riskScore', e.target.value)}
                required
                min="1" // Add min/max for number input
                max="5"
                className="form-input block w-full rounded-md border border-gray-300 outline-none p-1.5 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" // Improved input styling
              />
            </div>
          </div>
        )}

        {/* Remove Button */}
        {reportEntries.length > 1 && expandedIndex === index && ( // Only show remove if more than one entry and expanded
          <Button
            type="button"
            danger
            onClick={() => handleRemoveEntry(index)}
            className="mt-6 bg-red-500 hover:bg-red-600 text-white font-bold rounded-md shadow-sm" // Increased top margin
          >
            Remove Observation
          </Button>
        )}
      </div>
    ))}

    {/* Add Entry Button */}
    <Button
      type="button"
      onClick={handleAddEntry}
      className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2.5 px-4 rounded-md shadow-sm transition-colors duration-300" // Adjusted padding
    >
      Add Another Observation
    </Button>

    {/* Submit Button */}
    <button
      type="submit"
      disabled={loading}
      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-md shadow-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed" // Adjusted padding
    >
      {loading ? 'Submitting...' : 'Submit Report'}
    </button>

    {error && <p className="text-red-500 text-center mt-4">{error}</p>}
  </form>
)}


      {activeTab === 'view' && (
        <div className="space-y-4">
            <div className="flex-row justify-between">
         <div></div>
       <Dropdown overlay={menu}>
         <Button>
           Download <DownOutlined />
         </Button>
       </Dropdown>
     </div>
          <h2 className="text-xl font-bold">Old Reports</h2>
          {oldReports.length === 0 ? (
            <p>No old reports available for this audit.</p>
          ) : (
            <div className="overflow-x-auto">

              <table className="table-auto w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="px-4 py-2 border">Observation</th>
                    <th className="px-4 py-2 border">Element</th>
                    <th className="px-4 py-2 border">Legal Standard</th>
                    <th className="px-4 py-2 border">Recommendation</th>

                    <th className="px-4 py-2 border">Risk</th>
                    <th className="px-4 py-2 border">Risk Score</th>
                  </tr>
                </thead>
                <tbody>
                  {oldReports.map((report, index) => (
                    <tr key={index} className="hover:bg-gray-100 text-center">
                      <td className="px-4 py-2 border">{report.observation}</td>
                      <td className="px-4 py-2 border">{report.element}</td>
                      <td className="px-4 py-2 border">{report.legalStandard}</td>
                      <td className="px-4 py-2 border">{report.recommendation}</td>

                      <td className={`px-4 py-2 border ${getRiskColor(report.risk)}`}>
                        {report.risk}
                      </td>
                      <td className={`px-4 py-2 border ${getRiskColor(report.risk)}`}>
                        {report.riskScore}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmitReportPage;
